// @ts-strict-ignore
import * as React from 'react';
import * as API from './../../api';
import Modal from 'antd/lib/modal/Modal';
import Form from 'antd/lib/form';
import Checkbox from 'antd/lib/checkbox';
import {
    defaultBuildFileTypesByAppStoreType,
    getIsApplePlatform,
    getIsOTAPlatform,
    getSeparatedWorkflowFields,
    isGooglePlatform,
    isIntegrationBuildDistroSupported,
    supportedBucketFilterFileTypes,
} from '../../helpers';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectBuildSystemIntegrations,
    selectVCSIntegration,
    selectAuthCredentials,
    selectDestinationInfoByDestinationStepConfigId,
    selectStepGraph,
    selectOrg,
} from '../../selectors/selectors';
import { BASE_BRANCH_FIELD_RULES, FieldType, FormFieldData, renderFormFields } from '../../helpers/formHelpers';
import Divider from 'antd/lib/divider';
import Switch from 'antd/lib/switch';
import {
    tryNewBuildDistributionBucket,
    tryUpdateBuildDistributionBucket,
    TRY_NEW_BUILD_DISTRIBUTION_BUCKET,
} from '../../actions/buildDistribution';
import { AddEditBucketMembers } from './AddEditBucketMembers';
import { globalMessage } from '../../utils/globalMessage';
import Select from 'antd/lib/select';
import {
    BuildDistroBucketBase,
    BucketRule,
    BuildDistroBucketRuleType,
    IntegrationType,
    PatternToken,
    WorkflowArgument,
    VCSUserData,
    BuildDistroFilterByVCSUsersBehaviorType,
    BuildDistroFilterByPRLabelsBehaviorType,
    RunwayOrVCSUser,
} from '../../types';
import { InfoCircleOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Popover from 'antd/lib/popover';
import Popconfirm from 'antd/lib/popconfirm';
import Tooltip from 'antd/lib/tooltip';
import { PatternTokenHelperTextComponent } from '../appSettings/PatternTokenHelperTextComponent';
import { CreateBuildDistributionBucketRequest } from '../../actions/entities';
import { BuildDistributionUpdatable } from '../../actions/updatable';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import { displayNameForBuildDistroBucketRuleType, displayUserNameShort } from '../../helpers/displayHelpers';
import { NamePath } from 'antd/lib/form/interface';
import {
    displayNameForIntegrationType,
    getArgumentsRecordFromArray,
    workflowFieldsFromBucketFormValue,
} from '../../helpers/integrationHelpers';
import Space from 'antd/lib/space';
import IntegrationIcon from '../icons/IntegrationIcon';
import { workflowArgumentField } from '../../helpers/integrationFormHelpers';
import { capitalize, uniqBy } from 'lodash';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { destinationsByIntegrationInstallationId } from '../Integrations';
import { useIntegrationSettingFields } from '@/utils/hooks/useIntegrationSettingFields';

const AVAILABLE_FILENAME_TOKENS: PatternToken[] = [
    PatternToken.Version,
    PatternToken.VersionConcise,
    PatternToken.ReleaseName,
    PatternToken.Wildcard,
];

const AVAILABLE_BRANCH_TOKENS: PatternToken[] = [
    PatternToken.Version,
    PatternToken.VersionConcise,
    PatternToken.ReleaseName,
    PatternToken.Wildcard,
];

const MAX_BUCKET_RULES = 1;
const INFO_ICON_MARGIN = 8;

const infoIcon = (marginInlineStart: number) => (
    <InfoCircleOutlined style={{ color: antGrey.grey6, marginInlineStart, cursor: 'default' }} />
);

const infoTooltip = (title: React.ReactNode) =>
    title ?
        {
            title: <Text style={{ color: runwayColors.white }}>{title}</Text>,
            icon: infoIcon(INFO_ICON_MARGIN),
        }
    :   undefined;

const tokensSupportedNote = (availableTokens: PatternToken[]) => (
    <Text type="secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: INFO_ICON_MARGIN }}>
        Tokens are supported
        <Popover
            overlayInnerStyle={{ width: 640, maxHeight: 200, overflowY: 'auto' }}
            content={<PatternTokenHelperTextComponent availableTokens={availableTokens} />}
        >
            {infoIcon(0)}
        </Popover>
    </Text>
);

const appSlugForWorkflow = (
    appSlugFromForm: string | null,
    workflowId: string,
    existingWorkflowData?: BucketRule['workflowData'],
): string | null => {
    if (appSlugFromForm) {
        return appSlugFromForm;
    }

    if (existingWorkflowData?.workflowId === workflowId || existingWorkflowData?.workflowName === workflowId) {
        return existingWorkflowData.appSlug ?? null;
    }

    return null;
};

const workflowFormValueFromRule = (rule: BucketRule, hasWorkflowPicker: boolean, integrationType?: IntegrationType) => {
    if (!rule.workflowData) {
        return undefined;
    }

    if (rule.workflowData.appSlug) {
        if (!hasWorkflowPicker && integrationType === IntegrationType.AzureCI) {
            return `${rule.workflowData.appSlug},${rule.workflowData.workflowId}`;
        }

        return [rule.workflowData.appSlug, rule.workflowData.workflowId];
    }

    if (!hasWorkflowPicker) {
        return rule.workflowData.workflowName || rule.workflowData.workflowId;
    }

    return rule.workflowData.workflowId;
};

export interface CreateEditBuildBucketModalProps {
    bucket?: BuildDistroBucketBase;
    open: boolean;
    onCancel: () => void;
    appId: string;
    orgId: string;
}

const RunwayOrVCSUserLabel: React.FC<{ runwayOrVCSUser: RunwayOrVCSUser; vcsIntegrationId: IntegrationType }> = ({
    runwayOrVCSUser: { vcsUser, user },
    vcsIntegrationId,
}) => {
    const name = user && displayUserNameShort(user);

    const vcsIcon = vcsIntegrationId && <IntegrationIcon size={20} integrationId={vcsIntegrationId} />;

    return (
        <Space align="center">
            {vcsIcon}
            <Text>{vcsUser.username}</Text>
            {name && <Text type="secondary">{`(${name})`}</Text>}
        </Space>
    );
};

export const CreateEditBuildBucketModal: React.FC<CreateEditBuildBucketModalProps> = ({
    appId,
    orgId,
    open,
    onCancel,
    bucket,
}) => {
    const [form] = Form.useForm();

    const isEditing = !!bucket;

    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const buildSystemIntegrations = useSelector((state: RootState) => selectBuildSystemIntegrations(state, appId));
    const vcsSystemIntegration = useSelector((state: RootState) => selectVCSIntegration(state, appId));
    const selectedBuildSystemIntegrationId = Form.useWatch('buildSystemIntegrationInstallationId', form);
    const defaultBuildSystemIntegrationId = buildSystemIntegrations[0]?.installationId;
    const effectiveBuildSystemIntegrationId =
        selectedBuildSystemIntegrationId ??
        bucket?.buildSystemIntegrationInstallationId ??
        defaultBuildSystemIntegrationId;
    const buildSystemIntegration = buildSystemIntegrations.find(
        (integration) => integration.installationId === effectiveBuildSystemIntegrationId,
    );
    const destinationInfoByDestinationStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );
    const stepGraph = useSelector((state: RootState) => selectStepGraph(state, appId));
    const orgTags = useSelector((state: RootState) => selectOrg(state)?.tags ?? []);
    const destinationsByIntegrationId = React.useMemo(
        () => destinationsByIntegrationInstallationId(stepGraph, destinationInfoByDestinationStepConfigId, orgTags),
        [stepGraph, destinationInfoByDestinationStepConfigId, orgTags],
    );
    const requiresSupportedBuildSystemIntegration =
        buildSystemIntegration ? !isIntegrationBuildDistroSupported(buildSystemIntegration.id) : true;
    const buildSystemIntegrationOptions = buildSystemIntegrations.map((integration) => {
        const alias =
            typeof integration.alias === 'string' && integration.alias.trim().length > 0 ?
                integration.alias.trim()
            :   null;
        const destinations = destinationsByIntegrationId[integration.installationId] ?? [];

        return {
            value: integration.installationId,
            searchText: [integration.name, alias, displayNameForIntegrationType(integration.id)]
                .filter(Boolean)
                .join(' '),
            label: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <Space size={4}>
                        <Text>{integration.name}</Text>
                        {alias && <Text type="secondary">{`(${alias})`}</Text>}
                    </Space>
                    <DestinationCounter countOnly size={20} destinations={destinations} />
                </div>
            ),
        };
    });

    const hasVCSIntegration = !!vcsSystemIntegration;

    // CI integration not required in form if app has no integrations
    const hasBuildSystemIntegrations = buildSystemIntegrationOptions.length > 0;

    // TODO - Bitrise pipelines support for BD
    //const selectedBitriseIsPipelineDev = useWatch(['bitriseIsPipelineDev'], form);

    const { settingFields: buildSystemSettingFields } = useIntegrationSettingFields(
        orgId,
        appId,
        buildSystemIntegration?.installationId,
    );
    const { workingBranchWorkflowFields } = getSeparatedWorkflowFields(buildSystemSettingFields, true);

    const dispatch = useDispatch();

    const isCreatingNewBuildDistributionBucket = useIsLoading(TRY_NEW_BUILD_DISTRIBUTION_BUCKET);
    const buildDistributionBucketError = useError(TRY_NEW_BUILD_DISTRIBUTION_BUCKET);
    const createBuildDistributionBucketSuccess = useUpdated(TRY_NEW_BUILD_DISTRIBUTION_BUCKET);

    const isUpdatingBuildDistributionBucket = useIsLoading(
        BuildDistributionUpdatable.UpdateBuildDistroBucket,
        bucket?.id,
    );
    const updateBuildDistributionBucketError = useError(BuildDistributionUpdatable.UpdateBuildDistroBucket, bucket?.id);
    const updateBuildDistributionBucketSuccess = useUpdated(
        BuildDistributionUpdatable.UpdateBuildDistroBucket,
        bucket?.id,
    );

    React.useEffect(() => {
        if (createBuildDistributionBucketSuccess) {
            globalMessage.success('Bucket created successfully!');
        }
        if (updateBuildDistributionBucketSuccess) {
            globalMessage.success('Bucket updated successfully!');
        }

        onCancel();
    }, [createBuildDistributionBucketSuccess, updateBuildDistributionBucketSuccess]);

    React.useEffect(() => {
        if (buildDistributionBucketError) {
            globalMessage.error(buildDistributionBucketError);
        }

        if (updateBuildDistributionBucketError) {
            globalMessage.error(updateBuildDistributionBucketError);
        }
    }, [buildDistributionBucketError, updateBuildDistributionBucketError]);

    React.useEffect(() => {
        if (open) {
            form.resetFields();
        }
    }, [open, bucket?.id, form]);

    const [repoContributors, setRepoContributors] = React.useState<VCSUserData[]>([]);
    const [appUsersWithVCSUsers, setAppUsersWithVCSUsers] = React.useState<RunwayOrVCSUser[]>([]);

    const allRunwayOrVCSUsers: RunwayOrVCSUser[] = uniqBy(
        [...appUsersWithVCSUsers, ...repoContributors.map((contributor) => ({ vcsUser: contributor }))],
        (runwayOrVCSUser) => runwayOrVCSUser.vcsUser.username,
    );

    const authCreds = useSelector((state: RootState) => selectAuthCredentials(state));

    React.useEffect(() => {
        if (open) {
            API.getAppRepoContributors(
                {
                    appId: appId,
                    orgId: orgId,
                    bucketId: bucket?.id,
                },
                authCreds,
            )
                .then((response) => {
                    setRepoContributors(response.contributors ?? []);
                    setAppUsersWithVCSUsers(response.vcsUsers ?? []);
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }, [open, authCreds]);

    const createEditBucket = (values) => {
        const requestData = {
            ...values,
            appId,
            orgId,
            buildSystemIntegrationInstallationId: values.buildSystemIntegrationInstallationId,
            rules: values?.rules?.map((rule, index) => {
                const workflowArguments = getArgumentsRecordFromArray(rule.workflowArguments);
                const workflowData = rule.workflowData;
                const existingWorkflowData = bucket?.rules?.[index]?.workflowData;
                const savedFilterByVCSUsers = bucket?.rules?.[index]?.filterByVCSUsers ?? [];
                const filterByVCSUsers = uniqBy(
                    (rule.filterByVCSUsersUsernames ?? [])
                        .map(
                            (username) =>
                                allRunwayOrVCSUsers.find((user) => user.vcsUser.username === username)?.vcsUser ??
                                savedFilterByVCSUsers.find((vcsUser) => vcsUser.username === username),
                        )
                        .filter((vcsUser) => !!vcsUser),
                    (vcsUser: VCSUserData) => vcsUser.username,
                );
                const excludeAdditionalArtifactPatterns =
                    rule.pullInAdditionalArtifacts ? (rule.excludeAdditionalArtifactPatterns ?? []) : [];
                if (workflowData) {
                    const { workflowId, appSlug } = workflowFieldsFromBucketFormValue(workflowData);

                    let subWorkflowId = rule.subWorkflowId;
                    if (subWorkflowId == '') {
                        subWorkflowId = null;
                    }

                    return {
                        ...rule,
                        workflowData: {
                            ...rule.workflowData,
                            workflowId: workflowId,
                            subWorkflowId: subWorkflowId,
                            appSlug: appSlugForWorkflow(appSlug, workflowId, existingWorkflowData),
                            variables: workflowArguments,
                        },
                        filterByVCSUsers: filterByVCSUsers,
                        filterByVCSUsersBehavior: rule.filterByVCSUsersBehavior,
                        excludeAdditionalArtifactPatterns,
                    };
                }

                return {
                    ...rule,
                    filterByVCSUsers: filterByVCSUsers,
                    filterByVCSUsersBehavior: rule.filterByVCSUsersBehavior,
                    excludeAdditionalArtifactPatterns,
                };
            }),
        };

        dispatch(
            isEditing ?
                tryUpdateBuildDistributionBucket(BuildDistributionUpdatable.UpdateBuildDistroBucket, {
                    bucketId: bucket.id,
                    ...requestData,
                })
            :   tryNewBuildDistributionBucket({
                    ...requestData,
                } as CreateBuildDistributionBucketRequest),
        );
    };

    const name = Form.useWatch('name', form);
    const rules = Form.useWatch('rules', form);

    const orgWideAccessEnabled = Form.useWatch('orgWideAccessEnabled', form);
    const publicBucketLinkEnabled = Form.useWatch('publicBucketLinkEnabled', form);

    const defaultRule: BucketRule = {
        type: undefined,
        fileFilterPatterns: [],
        branch: null,
        filterByVCSUsers: [],
        pullInAdditionalArtifacts: false,
        excludeAdditionalArtifactPatterns: [],
        filterByVCSUsersBehavior: BuildDistroFilterByVCSUsersBehaviorType.Include,
        filterByPRLabels: [],
        filterByPRLabelsBehavior: BuildDistroFilterByPRLabelsBehaviorType.AnyOf,
    };

    const isIos = getIsApplePlatform(platform);
    const isAndroid = isGooglePlatform(platform);
    const isOTA = getIsOTAPlatform(platform);

    const hasReachedMaxRulesLimit = rules?.length >= MAX_BUCKET_RULES;
    const showRuleNumber = MAX_BUCKET_RULES > 1;

    return (
        <Modal
            width={600}
            open={open}
            title={isEditing ? `Edit bucket` : 'Create bucket'}
            okText={isEditing ? 'Save' : `Create ${name ? `'${name}' bucket` : 'bucket'}`}
            onOk={() => {
                form.validateFields()
                    .then(() => {
                        form.submit();
                    })
                    .catch(() => null);
            }}
            okButtonProps={{
                loading: isCreatingNewBuildDistributionBucket || isUpdatingBuildDistributionBucket,
                disabled: !name || (hasBuildSystemIntegrations && !effectiveBuildSystemIntegrationId),
            }}
            onCancel={onCancel}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={
                    isEditing ?
                        {
                            name: bucket?.name,
                            buildSystemIntegrationInstallationId:
                                bucket?.buildSystemIntegrationInstallationId ?? defaultBuildSystemIntegrationId,
                            orgWideAccessEnabled: bucket?.orgWideAccessEnabled,
                            publicBucketLinkEnabled: bucket?.publicBucketLinkEnabled,
                            rules: bucket?.rules.map((rule) => {
                                const workflowArguments: Record<string, string> = rule.workflowData?.variables;
                                const argumentsForWorkflow: WorkflowArgument[] = [];
                                if (workflowArguments) {
                                    for (const [key, value] of Object.entries(workflowArguments)) {
                                        argumentsForWorkflow.push({
                                            argumentKey: key,
                                            argumentValue: value,
                                        });
                                    }
                                }
                                return {
                                    ...rule,
                                    workflowData: workflowFormValueFromRule(
                                        rule,
                                        workingBranchWorkflowFields.length > 0,
                                        buildSystemIntegration?.id,
                                    ),
                                    subWorkflowId: rule.workflowData?.subWorkflowId,
                                    workflowArguments: argumentsForWorkflow,
                                    filterByVCSUsersUsernames:
                                        rule.filterByVCSUsers?.map((user) => user.username) ?? [],
                                    filterByVCSUsersBehavior:
                                        rule.filterByVCSUsersBehavior ??
                                        BuildDistroFilterByVCSUsersBehaviorType.Include,
                                    filterByPRLabels: rule.filterByPRLabels ?? [],
                                    filterByPRLabelsBehavior:
                                        rule.filterByPRLabelsBehavior ?? BuildDistroFilterByPRLabelsBehaviorType.AnyOf,
                                    excludeAdditionalArtifactPatterns: rule.excludeAdditionalArtifactPatterns ?? [],
                                };
                            }),
                            members: bucket?.members,
                        }
                    :   {
                            name: 'New bucket',
                            buildSystemIntegrationInstallationId: defaultBuildSystemIntegrationId,
                            orgWideAccessEnabled: false,
                            publicBucketLinkEnabled: false,
                            members: [],
                            rules: [],
                        }
                }
                onFinish={createEditBucket}
            >
                <Form.Item
                    label="Bucket name"
                    name="name"
                    rules={[
                        {
                            required: true,
                            message: 'Please enter a bucket name',
                        },
                    ]}
                >
                    <Input placeholder="Enter a bucket name" />
                </Form.Item>
                {isEditing && (
                    <Space style={{ marginTop: 4, marginBottom: 24 }} align="center">
                        <Text type="secondary">Bucket identifier: </Text>
                        <Text
                            style={{
                                backgroundColor: antGrey.grey5,
                                color: runwayColors.darkTextGrey,
                                borderRadius: 3,
                                borderStyle: 'solid',
                                borderColor: antGrey.grey7,
                                borderWidth: 1,
                                padding: '2px 4px',
                            }}
                        >
                            {bucket.id}
                        </Text>
                    </Space>
                )}
                <Form.Item
                    label="CI integration"
                    name="buildSystemIntegrationInstallationId"
                    rules={[
                        {
                            required: hasBuildSystemIntegrations,
                            message: 'Please select a CI integration',
                        },
                    ]}
                >
                    <Select
                        showSearch
                        placeholder={
                            hasBuildSystemIntegrations ? 'Select a CI integration' : 'No CI integrations connected'
                        }
                        options={buildSystemIntegrationOptions}
                        filterOption={(input, option) =>
                            `${option?.searchText ?? ''}`.toLowerCase().includes(input.toLowerCase())
                        }
                        disabled={!hasBuildSystemIntegrations}
                    />
                </Form.Item>
                <Divider />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <Text>Bucket rules</Text>
                    <Tooltip title="Configure rules that determine which builds automatically get pulled into this bucket from CI workflows.">
                        {infoIcon(0)}
                    </Tooltip>
                </div>
                {requiresSupportedBuildSystemIntegration && (
                    <Text type="warning">
                        Connect a supported build system integration to add a rule for this bucket
                    </Text>
                )}
                <Form.List name="rules">
                    {(fields, { add, remove }) => {
                        return (
                            <>
                                <Form.Item
                                    shouldUpdate
                                    noStyle
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 12,
                                        marginTop: 12,
                                    }}
                                >
                                    {() =>
                                        fields.map((field, index) => {
                                            const workflowFields: FormFieldData[] =
                                                workingBranchWorkflowFields.length === 0 ?
                                                    [
                                                        {
                                                            fieldType: FieldType.Input,
                                                            disabled: requiresSupportedBuildSystemIntegration,
                                                            placeholder: 'build',
                                                        },
                                                    ]
                                                :   workingBranchWorkflowFields;

                                            const mappedFields = workflowFields.map((f, i) => {
                                                // a bit of a hacky way to get the right label given integrations call workflows different things, and to account for sub workflow (Jenkins)
                                                const label =
                                                    f.label ?
                                                        capitalize((f.label as string).split(' ').pop())
                                                    :   'Workflow';

                                                // since we take working branch fields, all are optional, ensure first required.
                                                const rules = i == 0 ? [{ required: true }] : [...(f.rules ?? [])];

                                                // if i > 0, Jenkins sub workflow case
                                                const namePath = i == 0 ? 'workflowData' : 'subWorkflowId';

                                                return {
                                                    ...f,
                                                    helpMessage: null,
                                                    label: label,
                                                    name: [field.name, namePath] as string & NamePath,
                                                    extraData: [
                                                        {
                                                            placeholder: 'Select workflow',
                                                            allowClear: true,
                                                        },
                                                    ],
                                                    rules: rules,
                                                    style: { marginTop: 0, marginBottom: 12 },
                                                };
                                            });

                                            const ruleType = form.getFieldValue(['rules', field.name, 'type']);
                                            const isBranchRule = ruleType === BuildDistroBucketRuleType.Branch;
                                            const isPRRule = ruleType === BuildDistroBucketRuleType.PR;
                                            const pullsInAdditionalArtifacts = form.getFieldValue([
                                                'rules',
                                                field.name,
                                                'pullInAdditionalArtifacts',
                                            ]);

                                            const requiresVCSIntegration = !hasVCSIntegration;

                                            const argumentsField =
                                                buildSystemIntegration ?
                                                    shouldShowWorkflowArgumentsField(buildSystemIntegration.id) ?
                                                        workflowArgumentField(null, null, [
                                                            field.name,
                                                            'workflowArguments',
                                                        ] as string & NamePath)
                                                    :   null
                                                :   null;

                                            const branchField: FormFieldData = {
                                                label: isBranchRule ? 'Branch' : 'Base branch',
                                                name: [field.name, isBranchRule ? 'branch' : 'baseBranch'] as string &
                                                    NamePath,
                                                fieldType: FieldType.Input,
                                                rules: [
                                                    ...BASE_BRANCH_FIELD_RULES,
                                                    {
                                                        required: true,
                                                    },
                                                ],
                                                disabled: requiresSupportedBuildSystemIntegration,
                                                placeholder: 'main',
                                                helpMessage:
                                                    isBranchRule ?
                                                        tokensSupportedNote(AVAILABLE_BRANCH_TOKENS)
                                                    :   <Text type="secondary">Tokens are not supported</Text>,
                                                style: { marginTop: 0, marginBottom: 28 },
                                            };

                                            const helperText =
                                                isBranchRule ?
                                                    'Build artifacts will be automatically pulled into the bucket from matching workflow runs on the configured branch.'
                                                :   'Build artifacts will be automatically pulled into the bucket from matching workflow runs on any pull requests that are open against the base branch.';
                                            const userHelpText =
                                                isBranchRule ?
                                                    `Filter by commit author – only builds triggered by commits authored by one of the specified users will be pulled into the bucket.`
                                                :   vcsSystemIntegration &&
                                                    `Filter ${
                                                        isBranchRule ? 'branches' : 'PRs'
                                                    } to those created by the specified users. Select one or more users or enter a ${displayNameForIntegrationType(
                                                        vcsSystemIntegration.id,
                                                    )} username.`;

                                            return (
                                                <div
                                                    key={field.key}
                                                    style={{
                                                        width: '100%',
                                                        backgroundColor: antGrey.grey2,
                                                        borderRadius: 8,
                                                        padding: 20,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 12,
                                                        }}
                                                    >
                                                        {showRuleNumber && (
                                                            <div
                                                                style={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    borderRadius: 12,
                                                                    backgroundColor: runwayColors.primary,
                                                                    color: 'white',
                                                                    display: 'flex',
                                                                    justifyContent: 'center',
                                                                    alignItems: 'center',
                                                                    flexShrink: 0,
                                                                    lineHeight: 1,
                                                                    fontSize: 13,
                                                                }}
                                                            >
                                                                {index + 1}
                                                            </div>
                                                        )}
                                                        <div style={{ flex: 1 }}>
                                                            <Form.Item
                                                                name={[field.name, 'type']}
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: 'Please select a rule type',
                                                                    },
                                                                    {
                                                                        validator: async (_, value) => {
                                                                            if (
                                                                                !hasVCSIntegration &&
                                                                                value === BuildDistroBucketRuleType.PR
                                                                            ) {
                                                                                return Promise.reject(
                                                                                    new Error(
                                                                                        'Cannot add pull request rules without a VCS integration',
                                                                                    ),
                                                                                );
                                                                            }
                                                                            return Promise.resolve();
                                                                        },
                                                                    },
                                                                ]}
                                                                noStyle
                                                            >
                                                                <Select
                                                                    style={{ width: '100%' }}
                                                                    placeholder="Select a rule type"
                                                                    options={Object.values(
                                                                        BuildDistroBucketRuleType,
                                                                    ).map((type) => {
                                                                        return {
                                                                            label: displayNameForBuildDistroBucketRuleType(
                                                                                type,
                                                                            ),
                                                                            value: type,
                                                                        };
                                                                    })}
                                                                />
                                                            </Form.Item>
                                                        </div>
                                                        <Popconfirm
                                                            title="Delete this rule?"
                                                            description="This bucket will have no rules until you add another."
                                                            onConfirm={() => remove(index)}
                                                            okButtonProps={{ danger: true }}
                                                            okText="Delete"
                                                            placement="left"
                                                        >
                                                            <Button
                                                                icon={
                                                                    <DeleteOutlined
                                                                        style={{ color: runwayColors.statusRed }}
                                                                    />
                                                                }
                                                                danger
                                                                type="link"
                                                            />
                                                        </Popconfirm>
                                                    </div>
                                                    {form.getFieldError(['rules', field.name, 'type']).length > 0 && (
                                                        <Text type="danger" style={{ display: 'block', marginTop: 4 }}>
                                                            {form.getFieldError(['rules', field.name, 'type'])[0]}
                                                        </Text>
                                                    )}
                                                    {ruleType && (
                                                        <div style={{ marginTop: 12 }}>
                                                            <Text
                                                                type="secondary"
                                                                style={{ display: 'block', marginBottom: 28 }}
                                                            >
                                                                {helperText}
                                                            </Text>
                                                            <div style={{ marginBottom: 28 }}>
                                                                {renderFormFields(mappedFields)}
                                                                {argumentsField && (
                                                                    <div style={{ marginBottom: -16 }}>
                                                                        {renderFormFields([argumentsField])}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {renderFormFields([branchField])}
                                                            {isPRRule && (
                                                                <div style={{ marginBottom: 28 }}>
                                                                    <div
                                                                        style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 8,
                                                                            marginBottom: 8,
                                                                        }}
                                                                    >
                                                                        <Text>PR labels</Text>
                                                                        <Tooltip title="Filter PRs by label. Use newlines to enter multiple labels.">
                                                                            {infoIcon(0)}
                                                                        </Tooltip>
                                                                    </div>
                                                                    <div style={{ display: 'flex', gap: 16 }}>
                                                                        <Form.Item
                                                                            name={[
                                                                                field.name,
                                                                                'filterByPRLabelsBehavior',
                                                                            ]}
                                                                            style={{ marginBottom: 0, width: 120 }}
                                                                        >
                                                                            <Select
                                                                                style={{ width: '100%' }}
                                                                                options={[
                                                                                    {
                                                                                        label: 'any of',
                                                                                        value: BuildDistroFilterByPRLabelsBehaviorType.AnyOf,
                                                                                    },
                                                                                    {
                                                                                        label: 'none of',
                                                                                        value: BuildDistroFilterByPRLabelsBehaviorType.NoneOf,
                                                                                    },
                                                                                    {
                                                                                        label: 'all of',
                                                                                        value: BuildDistroFilterByPRLabelsBehaviorType.AllOf,
                                                                                    },
                                                                                ]}
                                                                            />
                                                                        </Form.Item>
                                                                        <Form.Item
                                                                            name={[field.name, 'filterByPRLabels']}
                                                                            style={{ marginBottom: 0, flex: 1 }}
                                                                        >
                                                                            <Select
                                                                                mode="tags"
                                                                                allowClear
                                                                                tokenSeparators={['\n']}
                                                                                dropdownStyle={{ display: 'none' }}
                                                                                style={{ width: '100%' }}
                                                                            />
                                                                        </Form.Item>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            <div style={{ marginBottom: 28 }}>
                                                                <div
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 8,
                                                                        marginBottom: 8,
                                                                    }}
                                                                >
                                                                    <Text>Users</Text>
                                                                    {userHelpText && (
                                                                        <Tooltip title={userHelpText}>
                                                                            {infoIcon(0)}
                                                                        </Tooltip>
                                                                    )}
                                                                </div>
                                                                <div style={{ display: 'flex', gap: 16 }}>
                                                                    <Form.Item
                                                                        name={[field.name, 'filterByVCSUsersBehavior']}
                                                                        style={{ marginBottom: 0, width: 120 }}
                                                                    >
                                                                        <Select
                                                                            disabled={requiresVCSIntegration}
                                                                            style={{ width: '100%' }}
                                                                            options={[
                                                                                {
                                                                                    label: 'any of',
                                                                                    value: BuildDistroFilterByVCSUsersBehaviorType.Include,
                                                                                },
                                                                                {
                                                                                    label: 'none of',
                                                                                    value: BuildDistroFilterByVCSUsersBehaviorType.Exclude,
                                                                                },
                                                                            ]}
                                                                        />
                                                                    </Form.Item>
                                                                    <Form.Item
                                                                        name={[field.name, 'filterByVCSUsersUsernames']}
                                                                        style={{ marginBottom: 0, flex: 1 }}
                                                                    >
                                                                        <Select
                                                                            mode="tags"
                                                                            allowClear
                                                                            disabled={requiresVCSIntegration}
                                                                            style={{ width: '100%' }}
                                                                            options={allRunwayOrVCSUsers.map(
                                                                                (runwayOrVCSUser) => {
                                                                                    return {
                                                                                        label: (
                                                                                            <RunwayOrVCSUserLabel
                                                                                                key={
                                                                                                    runwayOrVCSUser
                                                                                                        .vcsUser
                                                                                                        .username
                                                                                                }
                                                                                                runwayOrVCSUser={
                                                                                                    runwayOrVCSUser
                                                                                                }
                                                                                                vcsIntegrationId={
                                                                                                    vcsSystemIntegration.id
                                                                                                }
                                                                                            />
                                                                                        ),
                                                                                        value: runwayOrVCSUser.vcsUser
                                                                                            .username,
                                                                                    };
                                                                                },
                                                                            )}
                                                                        />
                                                                    </Form.Item>
                                                                </div>
                                                            </div>
                                                            <Form.Item
                                                                label="File name(s)"
                                                                tooltip={infoTooltip(
                                                                    <>
                                                                        The file names of the artifact files that will
                                                                        be surfaced for each build. Unless unset by the
                                                                        user, the default configuration is that all{' '}
                                                                        {defaultBuildFileTypesByAppStoreType(
                                                                            appStoreType,
                                                                        )}{' '}
                                                                        files will be surfaced.{' '}
                                                                        {!isOTA && (isIos || isAndroid) && (
                                                                            <>
                                                                                Supported file types:{' '}
                                                                                {supportedBucketFilterFileTypes().join(
                                                                                    ', ',
                                                                                )}
                                                                                .
                                                                            </>
                                                                        )}
                                                                    </>,
                                                                )}
                                                                extra={tokensSupportedNote(AVAILABLE_FILENAME_TOKENS)}
                                                                style={{
                                                                    width: '100%',
                                                                    marginBottom: 28,
                                                                }}
                                                                rules={[
                                                                    {
                                                                        validator: async (_, filenames) => {
                                                                            if (Array.isArray(filenames)) {
                                                                                const supportedFileTypes =
                                                                                    supportedBucketFilterFileTypes();
                                                                                for (const filename of filenames) {
                                                                                    if (
                                                                                        isOTA ||
                                                                                        !(isIos || isAndroid)
                                                                                    ) {
                                                                                        continue; // only validate for apple or android platforms, skip otherwise
                                                                                    }

                                                                                    // Don't tie the accepted extension to the app's single
                                                                                    // platform: "combined workflow" apps (migrated apps with
                                                                                    // both AppStoreConnect and GooglePlay destinations) can
                                                                                    // distribute both .ipa and .apk/.aab artifacts, and
                                                                                    // detecting that reliably on the frontend is difficult.
                                                                                    // Accept any known binary artifact extension.
                                                                                    const isValidFilename =
                                                                                        supportedFileTypes.some(
                                                                                            (fileType) =>
                                                                                                filename.endsWith(
                                                                                                    fileType,
                                                                                                ),
                                                                                        );

                                                                                    if (!isValidFilename) {
                                                                                        return Promise.reject(
                                                                                            `Artifact filenames must be of type ${supportedFileTypes.join(', ')}`,
                                                                                        );
                                                                                    }
                                                                                }
                                                                            }
                                                                            return Promise.resolve();
                                                                        },
                                                                    },
                                                                ]}
                                                                name={[field.name, 'fileFilterPatterns']}
                                                            >
                                                                <Select
                                                                    mode="tags"
                                                                    allowClear
                                                                    tokenSeparators={[',', '\n']}
                                                                    dropdownStyle={{ display: 'none' }}
                                                                    style={{ width: '100%' }}
                                                                    placeholder={isIos ? 'app.ipa' : 'app.apk'}
                                                                />
                                                            </Form.Item>
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                <Form.Item
                                                                    name={[field.name, 'pullInAdditionalArtifacts']}
                                                                    valuePropName="checked"
                                                                    noStyle
                                                                >
                                                                    <Checkbox>
                                                                        Include additional build artifacts
                                                                    </Checkbox>
                                                                </Form.Item>
                                                                <Tooltip title="All build artifacts produced by the configured workflow will be pulled in alongside each binary and surfaced as additional artifacts.">
                                                                    {infoIcon(0)}
                                                                </Tooltip>
                                                            </div>
                                                            {pullsInAdditionalArtifacts && (
                                                                <Form.Item
                                                                    label="Exclude additional build artifacts with file type(s)"
                                                                    tooltip={infoTooltip(
                                                                        <>
                                                                            Files matching any of these patterns will
                                                                            not be surfaced as additional artifacts.
                                                                            Enter a file type (.apk), a wildcard pattern
                                                                            (*-debug.apk), or an exact file name.
                                                                            Matching is case-insensitive and never
                                                                            affects the binary the bucket distributes.
                                                                        </>,
                                                                    )}
                                                                    extra={
                                                                        <Text type="secondary">
                                                                            Wildcards are accepted
                                                                        </Text>
                                                                    }
                                                                    style={{
                                                                        width: '100%',
                                                                        marginTop: 12,
                                                                        marginBottom: 0,
                                                                        marginInlineStart: 24,
                                                                    }}
                                                                    name={[
                                                                        field.name,
                                                                        'excludeAdditionalArtifactPatterns',
                                                                    ]}
                                                                >
                                                                    <Select
                                                                        mode="tags"
                                                                        allowClear
                                                                        tokenSeparators={[',', '\n']}
                                                                        dropdownStyle={{ display: 'none' }}
                                                                        style={{ width: '100%' }}
                                                                        placeholder={isIos ? '*.apk' : '*.ipa'}
                                                                    />
                                                                </Form.Item>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    }
                                </Form.Item>
                                {!hasReachedMaxRulesLimit && (
                                    <Button
                                        icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                        style={{ marginTop: 12 }}
                                        disabled={requiresSupportedBuildSystemIntegration}
                                        onClick={() => {
                                            add({
                                                ...defaultRule,
                                            });
                                        }}
                                    >
                                        Add a rule
                                    </Button>
                                )}
                            </>
                        );
                    }}
                </Form.List>

                <Divider />
                <Form.Item
                    label="Enable org-wide access"
                    name="orgWideAccessEnabled"
                    valuePropName="checked"
                    extra={
                        orgWideAccessEnabled ?
                            'Anyone in your Runway org with the bucket link can view and install.'
                        :   'Only bucket members can view and install.'
                    }
                >
                    <Switch />
                </Form.Item>
                <Form.Item
                    label="Enable public bucket link"
                    name="publicBucketLinkEnabled"
                    valuePropName="checked"
                    extra={
                        publicBucketLinkEnabled ?
                            <Text style={{ color: runwayColors.statusYellow }}>
                                Anyone with the public bucket link will be able to view a public version of the bucket
                                and install bucket builds on their device. The link can also be shared with others.
                            </Text>
                        :   'Only org members with bucket access will be able to view the bucket and install bucket builds on their device.'
                    }
                >
                    <Switch />
                </Form.Item>
                {!isEditing && (
                    <>
                        <Form.Item hidden name="members" />
                        <Divider />
                        <AddEditBucketMembers
                            initialMembers={[]}
                            excludedMembers={[]}
                            appId={appId}
                            orgId={orgId}
                            onMembersChange={(members = []) => {
                                form.setFieldsValue({ members } as CreateBuildDistributionBucketRequest);
                            }}
                        />
                    </>
                )}
            </Form>
        </Modal>
    );
};

const shouldShowWorkflowArgumentsField = (integration: IntegrationType): boolean => {
    if (integration == null) {
        return false;
    }
    switch (integration) {
        case IntegrationType.GitHubCI:
        case IntegrationType.GitLabCI:
        case IntegrationType.Codemagic:
        case IntegrationType.Bitrise:
        case IntegrationType.Buildkite:
        case IntegrationType.Jenkins:
        case IntegrationType.CircleCI:
        case IntegrationType.AzureCI:
            return true;
    }

    return false;
};
